import React, { FC, ReactElement, useState } from 'react';
import { useSelector } from 'react-redux';
import loadable from '@loadable/component';
import styled, { css } from 'styled-components';
import { button } from '@westwing/ui-kit/typography';
import { forDesktop } from '@westwing/ui-kit/breakPoints';
import StateInterface from '../../../redux/types';
import { Direction, StyledArrowIcon, StyledPlusIcon } from '../../Generic/Icons/StyledIcons';
import useTranslator from '../../../hooks/useTranslator';
import { pathFilterNames, ProductFilters } from '../../../redux/products/types';
import { FilterContainer } from '../../Overlays/FiltersFlyout/FiltersGroup';
import { scroll } from '../../GlobalStyles/scroll';
import { ActiveOverlayWrapper } from '../../Overlays/ContentOverlay/ActiveOverlayPointerWrapper';
import { useFilterState } from '../../../hooks/ListingPage/useFilterState';
import useListingPageUrlState from '../../../hooks/ListingPage/useListingPageUrlState';
import { getFiltersForPageType } from '../../Overlays/FiltersFlyout/filterValueService';
import { BellaTestIds } from '../../../../common/qaIds';

const DEFAULT_FILTERS_COUNT = 4;

const DropdownOverlay = loadable(() => import('../../Generic/Containers/DropdownOverlay'));

export interface FiltersProps {
    filters: ProductFilters[];
}

const FiltersContainer = styled.div`
    display: none;
    ${forDesktop`
        display: block;
        align-items: center;
    `}
`;

const FilterItemWrapper = styled.button`
    text-align: left;
    padding-right: 32px;
    display: flex;
    cursor: pointer;
    align-items: center;
    color: ${({ theme }) => theme.colors.charcoal100};
    box-sizing: border-box;
`;

const makeBold = css`
    font-family: ${({ theme }) => theme.fontBrandonMedium};
    line-height: 1.4;
    font-weight: 500;
`;
const DisplayName = styled.div<{ bold?: boolean }>`
    ${button}
    ${({ bold }) => (bold ? makeBold : '')}
`;

const IconButtonWrapper = styled.div`
    margin: 0 0 2px 5px;
    display: flex;
    align-self: center;
`;

const PlusIcon = styled(StyledPlusIcon)`
    &:before,
    &:after {
        width: 11px;
        height: 11px;
    }
    &:before {
        top: 6px;
        left: 5px;
    }
`;

const PlusIconWrapper = styled(IconButtonWrapper)`
    margin-bottom: 0;
`;

const OverlayWrapper = styled(ActiveOverlayWrapper)`
    padding: 15px;
    max-height: 400px;
    overflow: hidden;
    ${scroll('y', true)}
`;

const FilterItemContainer = styled.div`
    display: inline-block;
    margin-top: 15px;
`;

interface ExpandableFilterProps {
    filter: ProductFilters;
}

const ExpandableFilter: FC<ExpandableFilterProps> = ({ filter }) => {
    const [appliedFilter, onFilterValueChange] = useFilterState(filter);

    const [isExpanded, setIsExpanded] = useState(false);
    const arrowDirection = isExpanded ? Direction.Up : Direction.Down;

    const onExpand = () => {
        if (isExpanded) {
            return;
        }
        setIsExpanded(true);
    };

    const onClose = () => {
        setIsExpanded(false);
    };

    return (
        <FilterItemContainer data-testid={BellaTestIds.desktopFilterContainer}>
            <FilterItemWrapper onClick={onExpand}>
                <DisplayName bold={isExpanded}>{filter.displayName}</DisplayName>
                <IconButtonWrapper>
                    <StyledArrowIcon direction={arrowDirection} size={8} />
                </IconButtonWrapper>
            </FilterItemWrapper>
            {isExpanded && (
                <DropdownOverlay onOverlayClose={onClose} rightPosition={-151}>
                    <OverlayWrapper>
                        <FilterContainer
                            group={filter}
                            filterValue={appliedFilter.values}
                            handleValueChange={onFilterValueChange}
                        />
                    </OverlayWrapper>
                </DropdownOverlay>
            )}
        </FilterItemContainer>
    );
};

const DesktopFiltersStrip: FC = (): ReactElement => {
    const [urlState] = useListingPageUrlState();
    const filters = useSelector<StateInterface, ProductFilters[]>(state => state.products.filters);

    const allowedFilters = getFiltersForPageType(filters, urlState.listingPageType).filter(
        filter => filter.name !== pathFilterNames.categories
    );
    const activefilterLength =
        allowedFilters.length - DEFAULT_FILTERS_COUNT > 1 ? DEFAULT_FILTERS_COUNT : allowedFilters.length;
    const activeFilters = allowedFilters.slice(0, activefilterLength);
    const t = useTranslator();
    const [showHiddenFilters, setShowHiddenFilters] = useState(false);

    const showAllFilters = () => {
        setShowHiddenFilters(true);
    };

    const filtersToDisplay = showHiddenFilters ? allowedFilters : activeFilters;

    const hasMoreFiltersToShow = allowedFilters.length - DEFAULT_FILTERS_COUNT > 1;
    return (
        <FiltersContainer>
            {filtersToDisplay.map(filter => (
                <ExpandableFilter key={filter.name} filter={filter} />
            ))}
            <>
                {hasMoreFiltersToShow && !showHiddenFilters && (
                    <FilterItemContainer data-testid={BellaTestIds.moreFiltersButton}>
                        <FilterItemWrapper onClick={showAllFilters}>
                            <DisplayName bold>{t('More_filters')}</DisplayName>
                            <PlusIconWrapper>
                                <PlusIcon />
                            </PlusIconWrapper>
                        </FilterItemWrapper>
                    </FilterItemContainer>
                )}
            </>
        </FiltersContainer>
    );
};

export default DesktopFiltersStrip;
